import { LitElement, css, html, type PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';

import type { Vendor } from '@pedestal-panels/contracts';

import '@pedestal-panels/core';

import inventoryJson from '../inventory.json' with { type: 'json' };
import componentsJson from '../geometry/components.json' with { type: 'json' };
import primitivesJson from '../geometry/primitives.json' with { type: 'json' };

import {
  CHASSIS_VIEWBOX,
  bindSubstrate,
  injectSubstrateDefs,
  injectSubstrateStyles,
  parsePanelSvg,
  renderDerivedLabels,
} from './svg-substrate.ts';
import {
  candidateOverlayRequested,
  drawCandidateOverlay,
  enableInteractiveSelect,
} from './dev-candidate-overlay.ts';

// SVG → canonical affine, documented in inventory.json. Isotropic:
// maps the full PDF-vector panel bbox (111.85, 164.07, 115.69 × 402.17 —
// the 102 × 355 mm chassis incl. mounting flanges, spec p.57) onto the
// canonical 1020 × 3546 space. The single uniform scale is deliberate —
// it keeps the round knobs round and makes the HTML display overlays
// (positioned as a percentage of canonical) coincide exactly with the
// substrate's drawn windows; an anisotropic map would distort both.
const SVG_TO_CANONICAL = 8.8166;
const SVG_ORIGIN_X = 111.85;
const SVG_ORIGIN_Y = 164.07;

// Look up each indexed primitive's bbox once (the SVG-vbu rect the
// substrate extractor recorded). Used to anchor display overlays to
// the *actual* primitive shape the operator annotated, not the
// looser inventory documentation rect.
interface PrimBBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}
const PRIM_BBOX_BY_ID = new Map<string, PrimBBox>(
  primitivesJson.primitives.map((p) => [p.id, p.bbox] as const),
);

/**
 * Reduce a label or role string to a stable key so we can match
 * operator-authored descriptions ("master black / pedestal") against
 * inventory ids and labels ("display-master-black", "MASTER\nBLACK")
 * without forcing the operator to type the exact id.
 */
function normaliseRoleKey(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Build several lookup indices so each inventory display has the
// best chance of finding the operator's matching component. Order
// of preference: exact role match, normalised label/role match.
const COMPONENT_BY_ROLE = new Map<string, (typeof componentsJson.components)[number]>();
const COMPONENT_BY_NORMALISED_KEY = new Map<
  string,
  (typeof componentsJson.components)[number]
>();
for (const c of componentsJson.components) {
  if (c.role) COMPONENT_BY_ROLE.set(c.role, c);
  for (const candidate of [c.role, c.label]) {
    if (!candidate) continue;
    const key = normaliseRoleKey(candidate);
    if (key && !COMPONENT_BY_NORMALISED_KEY.has(key)) {
      COMPONENT_BY_NORMALISED_KEY.set(key, c);
    }
  }
}

function lookupBoundComponent(
  invId: string,
  invLabel: string,
): (typeof componentsJson.components)[number] | null {
  // Exact role match: the auto-annotator writes inventory id into
  // role, so the common case lands here.
  const exact = COMPONENT_BY_ROLE.get(invId);
  if (exact) return exact;
  // Normalised match: a manifest component whose label/role string
  // collapses (lowercase + strip non-alnum) to the same key as the
  // inventory id or label. Forgiving enough to catch label
  // formatting variation ('MASTER\nGAIN' vs 'master gain') without
  // the false positives an undirected substring match would invite.
  for (const candidate of [invId, invLabel]) {
    const key = normaliseRoleKey(candidate);
    if (key && COMPONENT_BY_NORMALISED_KEY.has(key)) {
      return COMPONENT_BY_NORMALISED_KEY.get(key) ?? null;
    }
  }
  return null;
}

interface DisplayOverlay {
  readonly id: string;
  readonly kind: string;
  readonly placeholder: string | null;
  readonly text: string | null;
  readonly mode: '' | 'placeholder' | 'static';
  readonly maxChars: number | null;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

const CANONICAL_W = 1020;
const CANONICAL_H = 3546;

/**
 * Pick the visual display kind for an inventory entry. Mirrors the
 * pre-substrate panel-rcp-3500 dispatch so the same display engine
 * lights up the same windows it always did.
 */
function displayKindFor(id: string): string {
  if (id === 'lcd-display') return 'lcd-colour';
  if (id === 'display-camera-number') return 'matrix-red';
  // ND, CC, MASTER GAIN, SHUTTER, IRIS, MASTER BLACK — Sony's
  // numeric 7-segment LED windows with measured italic lean. The
  // 'seven-segment' kind in panel-display renders proper segment
  // geometry (a..g + decimal point), with ghost-off segments
  // visible so the display reads as a fixed-character clock-style
  // window regardless of the active digit.
  return 'seven-segment';
}

function displayPlaceholderFor(id: string): string | null {
  if (id === 'lcd-display') return 'rcp-paint-status';
  if (id === 'display-camera-number') return 'rcp-camera-id';
  return null;
}

function displayStaticTextFor(id: string): string | null {
  switch (id) {
    case 'display-nd':
      return '1';
    case 'display-cc':
      return '6';
    case 'display-master-gain':
      return '0';
    case 'display-shutter':
      return '125';
    case 'display-iris':
      return '4.0';
    case 'display-master-black':
      return '0.0';
    default:
      return null;
  }
}

// Physical character capacity of each numeric window on the Sony
// hardware: ND and CC are single-digit; MASTER GAIN two digits; SHUTTER
// four (e.g. "1000"). IRIS and MASTER BLACK carry a decimal point
// ("4.0", "0.0") so they need three cells. The camera-number is drawn by
// its 8×8 dot-matrix bitmap placeholder, so its cap is nominal.
function displayMaxCharsFor(id: string): number | null {
  switch (id) {
    case 'display-shutter':
      return 4;
    case 'display-master-gain':
    case 'display-camera-number':
      return 2;
    case 'display-nd':
    case 'display-cc':
      return 1;
    case 'display-iris':
    case 'display-master-black':
      return 3;
    default:
      return null;
  }
}

/**
 * Resolve the overlay rect for an inventory display entry.
 *
 * The inventory's documented dimensions are conservative — they
 * encompass the whole display housing (bezel + label area + window).
 * The actual digit / dot-matrix glass is much smaller, and using the
 * outer rect makes the panel-display element scale text to absurd
 * proportions. When the operator has bound a primitive for this
 * display via components.json (matched by role = inventory id), we
 * use that primitive's bbox instead, converted from SVG vbu through
 * the documented affine. Falls back to inventory dimensions only
 * when no binding exists.
 */
function resolveOverlayRect(
  invId: string,
  invLabel: string,
  position: { x: number; y: number },
  dimensions: { w: number; h: number },
) {
  // Each display overlay is positioned from the PDF-vector window
  // primitive that build-bindings.mjs bound to it (matched by
  // role = inventory id). That primitive's bbox is Sony's own drawn
  // window, so the overlay coincides with it exactly. The earlier
  // hard-coded inventory-rect bypass existed only because the
  // flood-residual over-binding gave displays panel-spanning bboxes;
  // with one primitive per component that no longer happens. Inventory
  // dimensions remain the fallback when no binding resolves.
  // The LCD's bound primitive (p0044) is an oversized outer pane; the actual
  // glass window is p0057, inside the raised bezel. Anchor the touchscreen
  // overlay to p0057 so it sits within its true bounds.
  if (invId === 'lcd-display') {
    const bb = PRIM_BBOX_BY_ID.get('p0057-path');
    if (bb) {
      return {
        left: ((bb.x - SVG_ORIGIN_X) * SVG_TO_CANONICAL / CANONICAL_W) * 100,
        top: ((bb.y - SVG_ORIGIN_Y) * SVG_TO_CANONICAL / CANONICAL_H) * 100,
        width: (bb.width * SVG_TO_CANONICAL / CANONICAL_W) * 100,
        height: (bb.height * SVG_TO_CANONICAL / CANONICAL_H) * 100,
      };
    }
  }

  const comp = lookupBoundComponent(invId, invLabel);
  if (comp) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const pid of comp.sourcePrimitiveIds) {
      const bb = PRIM_BBOX_BY_ID.get(pid);
      if (!bb) continue;
      if (bb.x < minX) minX = bb.x;
      if (bb.y < minY) minY = bb.y;
      if (bb.x + bb.width > maxX) maxX = bb.x + bb.width;
      if (bb.y + bb.height > maxY) maxY = bb.y + bb.height;
    }
    if (Number.isFinite(minX)) {
      const canonX = (minX - SVG_ORIGIN_X) * SVG_TO_CANONICAL;
      const canonY = (minY - SVG_ORIGIN_Y) * SVG_TO_CANONICAL;
      const canonW = (maxX - minX) * SVG_TO_CANONICAL;
      const canonH = (maxY - minY) * SVG_TO_CANONICAL;
      return {
        left: (canonX / CANONICAL_W) * 100,
        top: (canonY / CANONICAL_H) * 100,
        width: (canonW / CANONICAL_W) * 100,
        height: (canonH / CANONICAL_H) * 100,
      };
    }
  }
  return {
    left: (position.x / CANONICAL_W) * 100,
    top: (position.y / CANONICAL_H) * 100,
    width: (dimensions.w / CANONICAL_W) * 100,
    height: (dimensions.h / CANONICAL_H) * 100,
  };
}

const DISPLAY_OVERLAYS: readonly DisplayOverlay[] = inventoryJson.controls
  // Digit / dot-matrix / LCD windows get a <panel-display> overlay. The
  // iris bargraph is deliberately excluded: it is not a glass window but
  // a row of 16 discrete red LEDs drawn directly in the vendor SVG. Those
  // segments render through the substrate's generic LED treatment
  // ([data-component-type='led']) bound via the iris-bargraph component —
  // the same dumb on/off mechanism every other LED on the panel uses.
  // What the segments indicate (iris position under the COARSE/SENS modes)
  // is control logic that lives outside the display layer and is not
  // modelled at Phase 1. Giving it a panel-display here only painted an
  // empty seven-segment ghost over the real LED strip.
  .filter(
    (c): c is typeof c & { type: string } =>
      typeof c.type === 'string' &&
      c.type.startsWith('display-') &&
      c.type !== 'display-led-bargraph',
  )
  .map((c) => {
    const placeholder = displayPlaceholderFor(c.id);
    const text = displayStaticTextFor(c.id);
    const rect = resolveOverlayRect(c.id, c.label, c.position, c.dimensions);
    return {
      id: c.id,
      kind: displayKindFor(c.id),
      placeholder,
      text,
      mode: text !== null ? ('static' as const) : placeholder !== null ? ('placeholder' as const) : ('' as const),
      maxChars: displayMaxCharsFor(c.id),
      ...rect,
    };
  });

/**
 * Sony RCP-3500 — Remote Control Panel, vendor-SVG substrate edition.
 *
 * This panel renders Sony's own front-face schematic (their A4
 * vendor SVG, cropped to the chassis face) directly inside the
 * shadow DOM. The operator-curated `components.json` manifest binds
 * each leaf primitive in that SVG to a logical control role — those
 * bindings appear on the live DOM as `data-component-id`,
 * `data-component-type`, `data-component-role`, `data-component-label`
 * and `data-component-block` attributes. Per-state styling is then a
 * pure CSS concern: change `data-component-state` and the SVG
 * primitive's fill / stroke flips to the new illumination palette.
 *
 * The pipeline:
 *
 *   1. `parsePanelSvg()` — DOMParser the vendor SVG into a detached
 *      `<svg>` element.
 *   2. Append it to the panel's shadow DOM, viewBox cropped to the
 *      full panel (111.85, 164.07, 115.69, 402.17 — the 102×355 mm
 *      chassis incl. flanges, see CHASSIS_VIEWBOX in svg-substrate).
 *   3. `injectSubstrateStyles()` — normalise the 0.25 vbu vendor
 *      strokes to ~1 px non-scaling so the schematic reads at panel
 *      scale.
 *   4. `bindSubstrate()` — walk the live SVG with the same
 *      filtering used by the annotator workbench, assign stable
 *      `data-prim-id`s, then tag every primitive named in
 *      `components.json` with its logical role.
 *
 * Geometry coordinates live in the substrate SVG's native page space
 * (A4 portrait). The RCP-3500 chassis is the rect
 * (111.85, 164.07, 115.69 × 402.17). Conversion to the panel's
 * canonical 1020 × 3546 vbu is the isotropic affine
 *   `vbu_x = (svg_x − 111.85) × 8.8166`
 *   `vbu_y = (svg_y − 164.07) × 8.8166`
 * but the substrate renders directly in SVG units — no conversion
 * needed for display, and no hand-measured positions either.
 */
export const meta = {
  id: 'rcp-3500',
  vendor: 'sony' as Vendor,
  model: 'RCP-3500',
  productName: 'Sony RCP-3500 Remote Control Panel',
} as const;

type PowerMode = 'off' | 'on' | 'test';

/** Live paint values shown on the colour LCD (the rcp-paint-status screen). */
interface PaintState {
  white: { r: number; g: number; b: number };
  black: { r: number; g: number; b: number };
  detail: number;
  ecs: string;
  cTemp: string;
  mWhite: string;
}

@customElement('panel-rcp-3500')
export class PanelRcp3500Element extends LitElement {
  /**
   * Panel power mode, driven by the stylised three-position switch beside
   * the panel. 'off' = dead (no glow, displays dark; moulded caps keep their
   * plastic colour), 'on' = standard operating (LCD and displays show their
   * normal values), 'test' = lamp test (every key, LED and display lit, the
   * LCD on colour bars). Default 'on'.
   */
  @state() private accessor mode: PowerMode = 'on';

  /**
   * Live paint readings shown on the colour LCD in 'on' mode. White / Black
   * R/G/B and Detail are driven by the panel's paint dials; the remaining
   * fields hold the neutral reference reading. Reassigning re-renders the LCD.
   */
  @state() private accessor paint: PaintState = {
    white: { r: 0, g: 0, b: 0 },
    black: { r: 0, g: 0, b: 0 },
    detail: 0,
    ecs: '60.00',
    cTemp: '4300K',
    mWhite: '0.0',
  };

  /**
   * Each bound key/LED's resting data-component-state, captured once after
   * the substrate binds, so returning to 'on' restores exactly the powered
   * default rather than guessing.
   */
  readonly #defaultStates = new Map<Element, string>();

  /** requestAnimationFrame handle for the TEST-mode lamp-test LED blink (0 = not running). */
  #ledBlinkId = 0;

  static override styles = [
    css`
      :host {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        container-type: inline-size;

        /* Sony RCP-3500 numeric paint readouts (ND, CC, MASTER GAIN,
         * SHUTTER, IRIS, MASTER BLACK) glow GREEN — verified against the
         * powered-on front-elevation photograph and documented in
         * inventory.json (every display-segment → green-segment #38e828).
         * The amber lit-function colour (--illumination-function-on)
         * belongs to the up/down adjustment BUTTONS beside each window,
         * NOT to the displays; an earlier build conflated the two and
         * rendered the numerals amber. --display-seg-on drives the
         * seven-segment glass in <panel-display> (inherits through the
         * shadow boundary as a custom property). */
        --display-seg-on: #38e828;
      }

      .stage {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3cqi;
        block-size: 100%;
        inline-size: 100%;
        padding: 1.2cqi;
        box-sizing: border-box;
      }

      /* Power switch — a stylised three-position selector that sits BESIDE
       * the panel (not on the chassis) and drives the whole panel's power
       * state. OFF: dead. ON: standard operating. TEST: lamp test. The
       * active position highlights and its status LED glows in its colour
       * (red / green / amber). */
      .power {
        display: flex;
        flex-direction: column;
        align-items: center;
        /* Sit at the TOP of the stage — level with the RCP's top edge (the
         * panel fills the stage height, so its top is the stage's top). */
        align-self: flex-start;
        gap: 0.5cqi;
        flex: 0 0 auto;
        user-select: none;
      }

      .power-title {
        font: 600 0.85cqi/1 var(--font-ui, system-ui, sans-serif);
        letter-spacing: 0.3em;
        color: #8a8a90;
        padding-inline-start: 0.3em;
      }

      .power-switch {
        display: flex;
        flex-direction: column;
        gap: 0.35cqi;
        padding: 0.7cqi;
        border-radius: 1.2cqi;
        background: linear-gradient(155deg, #2b2b30, #141416);
        border: 0.1cqi solid #0b0b0d;
        box-shadow:
          inset 0 0.12cqi 0.3cqi rgba(255, 255, 255, 0.07),
          inset 0 -0.12cqi 0.3cqi rgba(0, 0, 0, 0.6),
          0 0.4cqi 1cqi rgba(0, 0, 0, 0.55);
      }

      .power-pos {
        display: flex;
        align-items: center;
        gap: 0.8cqi;
        inline-size: 100%;
        padding: 0.8cqi 1.2cqi;
        border: 0.08cqi solid transparent;
        border-radius: 0.8cqi;
        background: transparent;
        color: #75757b;
        font: 700 1cqi/1 var(--font-ui, system-ui, sans-serif);
        letter-spacing: 0.12em;
        cursor: pointer;
        transition:
          color 160ms ease,
          background 160ms ease,
          box-shadow 160ms ease;
      }

      .power-pos:hover {
        color: #c8c8ce;
      }

      .power-pos:focus-visible {
        outline: 0.12cqi solid #5fa8ff;
        outline-offset: 0.12cqi;
      }

      .power-dot {
        inline-size: 0.85cqi;
        block-size: 0.85cqi;
        border-radius: 50%;
        background: #37373c;
        box-shadow: inset 0 0 0.15cqi rgba(0, 0, 0, 0.7);
        transition:
          background 160ms ease,
          box-shadow 160ms ease;
      }

      /* Active position: recessed highlight + its status LED glows. */
      .power-switch[data-mode='off'] .power-pos[data-pos='off'],
      .power-switch[data-mode='on'] .power-pos[data-pos='on'],
      .power-switch[data-mode='test'] .power-pos[data-pos='test'] {
        color: #f1f1f4;
        background: rgba(0, 0, 0, 0.35);
        border-color: rgba(255, 255, 255, 0.06);
        box-shadow:
          inset 0 0.15cqi 0.4cqi rgba(0, 0, 0, 0.6),
          inset 0 -0.1cqi 0.2cqi rgba(255, 255, 255, 0.05);
      }

      .power-switch[data-mode='off'] .power-pos[data-pos='off'] .power-dot {
        background: #ff4436;
        box-shadow:
          0 0 0.5cqi #ff4436,
          0 0 1.4cqi #ff4436;
      }

      .power-switch[data-mode='on'] .power-pos[data-pos='on'] .power-dot {
        background: #2fbf4d;
        box-shadow:
          0 0 0.5cqi #2fbf4d,
          0 0 1.4cqi #2fbf4d;
      }

      .power-switch[data-mode='test'] .power-pos[data-pos='test'] .power-dot {
        background: #f0b350;
        box-shadow:
          0 0 0.5cqi #f0b350,
          0 0 1.4cqi #f0b350;
      }

      /* The actual RCP-3500 is matt-black machined metal. Background
       * comes from the Sony vendor token surface
       * (--chassis-colour ≈ #1c1c1e) which cascades in via the host
       * element's data-vendor attribute. Aspect-ratio matches the full
       * PDF-vector panel crop (115.69 : 402.17 = 1:3.476), which is the
       * spec's 102 × 355 mm chassis (1:3.48) incl. mounting flanges.
       * preserveAspectRatio 'meet' fills the card with no letterbox and
       * no distortion — round knobs stay round. The proportion comes
       * from the documented dimensions (Operating Instructions p.57),
       * not from eyeballing a perspective-distorted photograph. */
      .panel {
        block-size: 100%;
        aspect-ratio: 115.69 / 402.17;
        max-inline-size: 100%;
        position: relative;
        background: var(--chassis-colour, #1c1c1e);
        box-shadow:
          0 0.4cqi 1cqi rgba(0, 0, 0, 0.5),
          inset 0 0.05cqi 0.2cqi rgba(255, 255, 255, 0.06);
      }

      .panel svg {
        position: absolute;
        inset: 0;
        block-size: 100%;
        inline-size: 100%;
        display: block;
        /* Isolate the heavy substrate as its own compositor layer: page scroll
         * and sibling repaints then composite a cached texture instead of
         * re-rasterising its ~95 SVG filters, and style/layout recalc is bounded
         * to this subtree. translateZ(0) is an identity transform — purely a
         * layer hint — so the rendered result is unchanged. */
        contain: layout style;
        transform: translateZ(0);
      }

      /* The vendor SVG renders dark ink (#231916) on the cream panel
       * by default. The substrate-injected <style> normalises stroke
       * widths via non-scaling-stroke. The rules below layer
       * per-state colour on top by targeting the component-binding
       * data attributes that bindSubstrate() applies. Everything is
       * additive to the original schematic — no geometry is invented,
       * only paint properties change. */

      /* Live silkscreen labels. bindSubstrate injects one <text> per
       * component whose manifest entry carries a labelBBox. The text
       * inherits SST from the vendor token surface, paints in the
       * same engrave colour as the vendor-outlined glyphs below it,
       * and never intercepts pointer events so the button beneath
       * stays clickable. font-size is set on the element itself
       * (derived from the bbox height by bindSubstrate); width / x
       * / y come from the geometry. */
      [data-label-overlay] {
        font-family: var(--font-ui);
        font-weight: 400;
        font-style: normal;
        fill: var(--chassis-engrave, #d8d4c8);
        stroke: none;
        pointer-events: none;
        user-select: none;
      }

      /* Per-primitive illumination uses the colour bindSubstrate
       * pulled out of inventory.illumination — exposed as the CSS
       * custom property --component-illum on the element itself.
       * data-component-state is set from the same inventory entry's
       * defaultState. Together they drive the cap-fill wash that
       * makes the panel read as powered-on. */

      /* Button caps — colours calibrated from the physical panel (chassis
       * black + Sony-logo white normalised, daylight + dim-room photos). Each
       * cap belongs to a family set as data-cap-family in firstUpdated; the
       * family fixes BOTH the moulded (unlit) plastic colour and the emissive
       * (lit) colour. Lit adds a discreet bloom in the lit colour. The default
       * (no family) is the off-white backlit key. !important overrides the
       * substrate's universal chassis-engrave fill inversion.
       *
       * Calibrated palette:
       *   off-white  base #d5dee4 (cool grey)   lit #e8a83a (soft amber)
       *   amber      base #e79500 (gold-amber)  lit #ff6400 (orange)
       *   green      base #1a8f69 (sea-green)   lit #19d396 (jade) — hue midway
       *              between the teal plastic and the green LED
       *   red        base #6e1a14 (oxblood)     lit #ff0a24 (pure red)
       */
      [data-component-type='button'],
      [data-component-type='assignable-button'] {
        cursor: pointer;
        transition: fill 120ms ease-out, fill-opacity 120ms ease-out,
          transform 55ms ease-out, filter 55ms ease-out;
        fill: #d5dee4 !important;
        fill-opacity: 0.9;
        /* Hard-plastic cap relief — crisp top sheen, see #rcp-cap-3d.
         * NOTE: no transform-box here — on these vendor primitives (which
         * sit inside nested <g transform> matrices) transform-box shifts
         * the cap off its geometry. The press uses a plain translateY,
         * which needs no transform-box. */
        filter: url(#rcp-cap-3d);
      }

      /* Hard-plastic press: on pointer-down the cap sinks (shading only — the
       * shrink itself is applied to the cap wrapper <g> in firstUpdated). */
      [data-component-type='button'][data-pressed] {
        filter: url(#rcp-cap-3d) brightness(0.88);
      }

      /* Off-white keys, lit — a translucent grey cap diffusing an amber LED
       * reads as a softer, yellower amber than the moulded amber caps. */
      [data-component-type='button'][data-component-state='on'],
      [data-component-type='button'][data-component-state='lit'],
      [data-component-type='assignable-button'][data-component-state='on'],
      [data-component-type='assignable-button'][data-component-state='lit'] {
        fill: #e8a83a !important;
        fill-opacity: 0.96;
        filter: url(#rcp-cap-3d) drop-shadow(0 0 0.18cqi #e8a83a) !important;
      }

      /* Physically-coloured caps — moulded in coloured plastic, so they keep
       * their colour even unlit (no glow); lit shifts to the emissive colour
       * plus a bloom. The family selectors below are placed AFTER the off-white
       * rules so equal-specificity matches resolve in their favour. */

      /* AMBER (PARA / MASTER / SUB ORDINATE). */
      [data-cap-family='amber'] { fill: #e79500 !important; }
      [data-cap-family='amber'][data-component-state='on'],
      [data-cap-family='amber'][data-component-state='lit'] {
        fill: #ff6400 !important;
        filter: url(#rcp-cap-3d) drop-shadow(0 0 0.18cqi #ff6400) !important;
      }

      /* GREEN (CAM PW / STANDARD / IRIS-MB ACTIVE / PANEL ACTIVE). */
      [data-cap-family='green'] { fill: #1a8f69 !important; }
      [data-cap-family='green'][data-component-state='on'],
      [data-cap-family='green'][data-component-state='lit'] {
        fill: #19d396 !important;
        filter: url(#rcp-cap-3d) drop-shadow(0 0 0.18cqi #19d396) !important;
      }

      /* RED (START·BREAK / WHITE / BLACK / STORE / CALL). */
      [data-cap-family='red'] { fill: #6e1a14 !important; }
      [data-cap-family='red'][data-component-state='on'],
      [data-cap-family='red'][data-component-state='lit'] {
        fill: #ff0a24 !important;
        filter: url(#rcp-cap-3d) drop-shadow(0 0 0.18cqi #ff0a24) !important;
      }

      /* Cap edge. A button cap is coloured hard plastic, not engraved
       * silkscreen, yet the substrate's universal
       * [data-prim-ink~='stroke'] inversion paints EVERY stroked
       * primitive in the off-white chassis-engrave colour — right for
       * labels, group frames and silkscreen rules, wrong on a bound
       * cap, where it draws a pale outline the real cap does not carry.
       * The two-attribute selector outranks that single-attribute
       * inversion, restoring the vendor's dark cap edge (#231916, the
       * ink the primitive itself declares) so the cap reads as a hard
       * plastic key seated in its well, not a traced outline. */
      [data-component-type='button'][data-prim-id],
      [data-component-type='assignable-button'][data-prim-id] {
        stroke: #231916 !important;
        stroke-width: 0.5px !important;
      }

      /* LEDs — discrete on/off. UNLIT: a translucent frosted off-white
       * window (an unpowered transparent LED), no outline — the exact
       * resting look of the unbound D EXT / EXT / F DROP indicator boxes
       * (FROSTED_LED_BOX_IDS in svg-substrate), which paint the same
       * --chassis-led-frost fill with stroke:none. LIT: the illumination
       * colour with a soft glow. The iris bargraph is an ordinary LED
       * here too — at rest (panel unpowered) it must read as dark glass,
       * not lit red, and only its segments that flip to state 'on' glow.
       * This rule also outranks the substrate's off-white stroke
       * inversion, so no "bold outline" survives. */
      [data-component-type='led'] {
        fill: var(--chassis-led-frost, rgba(216, 212, 200, 0.18)) !important;
        stroke: none !important;
      }
      [data-component-type='led'][data-component-state='on'] {
        fill: var(--component-illum, #2db84a) !important;
        fill-opacity: 0.9;
        filter: drop-shadow(0 0 0.6cqi var(--component-illum, #2db84a));
      }

      /* LED colour classes (data-led-set, assigned in svg-substrate). The set
       * fixes the lit colour — e.g. the iris bargraph is green, not red. In the
       * power switch's TEST mode #applyMode runs a lamp-test blink through each
       * set's states (an inline !important fill is the only thing that can
       * override these rules, so the blink is imperative, not a CSS animation). */
      [data-led-set='green'][data-component-state='on'] {
        fill: #20c038 !important;
        fill-opacity: 0.9;
        filter: drop-shadow(0 0 0.5cqi #20c038);
      }
      [data-led-set='amber'][data-component-state='on'] {
        fill: #e8a83a !important;
        fill-opacity: 0.9;
        filter: drop-shadow(0 0 0.5cqi #e8a83a);
      }
      [data-led-set='gar'][data-component-state='on'] {
        fill: #20c038 !important;
        fill-opacity: 0.9;
        filter: drop-shadow(0 0 0.5cqi #20c038);
      }

      /* Displays — sit recessed below the chassis surface. Slightly
       * darker than the chassis itself so they read as windows. NO outline:
       * the substrate's universal stroke inversion would otherwise trace each
       * window (ND / CC / MASTER GAIN / IRIS / MASTER BLACK / SHUTTER / CAMERA
       * NUMBER) in off-white — a recessed glass window has no drawn edge. */
      [data-component-type='display'] {
        fill: rgba(8, 10, 12, 0.95) !important;
        stroke: none !important;
      }

      /* Bezel rings around the iris lever, dial collars, etc. —
       * structural metal, no illumination, just chassis-engrave
       * stroke (provided by the universal substrate rule). */
      [data-component-type='bezel'] {
        /* fill stays at the chassis ink colour from substrate */
      }

      /* Iris control — the IRIS lever cap. The vendor PDF draws the
       * whole top-down assembly already (chassis well, master-black
       * ring, the index-mark ring and the centre pointer in their own
       * stroke primitives); the cap disc itself is a white placeholder
       * fill. We only flip that placeholder to the matte near-black of
       * the real hard-plastic disc — everything else is Sony's own
       * drawing, rendered straight. No gloss, no reconstructed cap. */
      [data-component-type='iris-control'] {
        fill: #161617 !important;
      }

      [data-component-type='encoder'],
      [data-component-type='dial'] {
        cursor: ns-resize;
      }
      /* Channel-coded rubber caps ONLY — the paint WHITE / BLACK-FLARE R/G/B
       * rotaries, whose inventory illumination name is cap-red / cap-green /
       * cap-blue. Fill tints the cap disc (the bound outer circle is stroke ink,
       * so this doesn't fight the substrate's stroke inversion) and the 3D
       * filter domes it like real polycarbonate. Every other knob stays bare:
       * DETAIL / SENS / COARSE have no cap colour, and the ASSIGNABLE's green is
       * a "when-assigned" state LED, NOT a resting cap colour — filling it green
       * was wrong. Restricting the filter here also stops it blooming a bare
       * knob's thin outline into a heavy ring. */
      [data-component-type='encoder'][data-illum-name^='cap-'] {
        fill: var(--component-illum);
        filter: url(#rcp-cap-3d);
      }

      /* The WHITE / BLACK-FLARE R/G/B rings are backlit rubber caps: when the
       * panel is powered (on/test) their channel colour reads brighter, more
       * saturated and with a soft glow; powered off they sit at the dim,
       * unlit rubber tone. */
      .panel[data-mode='on'] [data-component-type='encoder'][data-illum-name^='cap-'],
      .panel[data-mode='test'] [data-component-type='encoder'][data-illum-name^='cap-'] {
        filter: url(#rcp-cap-3d) brightness(1.45) saturate(1.5)
          drop-shadow(0 0 0.22cqi var(--component-illum));
      }

      /* Knob edge. Like a button cap, a rotary knob is a physical part,
       * not engraved silkscreen — left to the substrate's universal
       * stroke inversion its outline glows in the bright off-white
       * engrave colour, reading as a traced line-drawing rather than
       * the dark knurled plastic the photograph shows. Drop it to a
       * muted knob-metal grey: still legible against the chassis, no
       * longer a silkscreen-bright ring. The channel-coded R/G/B caps
       * keep the same muted rim over their colour fill + dome filter.
       * The two-attribute selector outranks the single-attribute
       * inversion; stroke-width stays at the substrate's pinned 1px. */
      [data-component-type='encoder'][data-prim-id],
      [data-component-type='dial'][data-prim-id] {
        stroke: #6e6c68 !important;
      }

      /* Labels — engraved silkscreen, stays as the original ink. */
      [data-component-type='label'] {
        /* no override */
      }

      /* Chassis / screw / separator — structural, no state. */
      [data-component-type='chassis'],
      [data-component-type='screw'],
      [data-component-type='separator'] {
        /* no override */
      }

      /* Ignored — purely informational. The operator's note that
       * "this primitive isn't a control" doesn't suppress the
       * geometry, because vendor-authored compound paths pack many
       * logically-distinct silkscreen / divider / label shapes into
       * a single <path> element (p0164-path alone holds 165 sub-
       * paths of letter geometry). Hiding the primitive would erase
       * dozens of unrelated marks the operator did not intend to
       * suppress. The tag survives in the manifest so a future
       * sub-path-splitting extractor can act on it precisely. */
      [data-component-type='ignored'] {
        /* metadata only */
      }

      /* Unbound indicator LEDs — the D EXT / EXT / F DROP frosted boxes and
       * the tiny activity dots near SAVE / network / alarm / cable. Tagged
       * data-led-indicator in svg-substrate so the power switch reaches them.
       * Frosted translucent glass at rest (an unpowered LED window). */
      [data-led-indicator='box'] {
        fill: var(--chassis-led-frost, rgba(216, 212, 200, 0.18)) !important;
        stroke: none !important;
      }
      [data-led-indicator='mini'] {
        fill: var(--chassis-led-frost-dim, rgba(216, 212, 200, 0.1)) !important;
        stroke: none !important;
      }

      /* ---- Power modes ------------------------------------------------- */

      /* OFF keeps the unbound indicator LEDs at their frosted resting look (the
       * physical lens stays visible, just unlit) — the same as ON. Buttons and
       * bound LEDs are driven to data-component-state='off' in #applyMode, so
       * moulded caps keep their plastic colour but nothing glows. So an OFF
       * panel shows all its physical structure with nothing illuminated. */

      /* TEST — lamp test. Every unbound indicator LED lit with a soft glow. */
      .panel[data-mode='test'] [data-led-indicator] {
        fill: #2fbf4d !important;
        filter: drop-shadow(0 0 0.45cqi #2fbf4d) !important;
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    this.dataset['vendor'] = meta.vendor;
  }

  override firstUpdated(): void {
    const slot = this.renderRoot.querySelector<HTMLDivElement>('.panel');
    if (!slot) return;

    const svg = parsePanelSvg();
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute(
      'viewBox',
      `${CHASSIS_VIEWBOX.x} ${CHASSIS_VIEWBOX.y} ${CHASSIS_VIEWBOX.width} ${CHASSIS_VIEWBOX.height}`,
    );

    // Drop any background the loaded SVG was carrying — the panel
    // card behind it provides the Sony chassis colour, and we want
    // the dark surface to read through any unfilled primitive area.
    svg.style.background = 'transparent';

    injectSubstrateDefs(svg);
    slot.appendChild(svg);

    // bindSubstrate uses getBBox / getComputedStyle, both of which
    // need the SVG to be in a rendered tree. The panel is now
    // attached, so this works without offscreen scaffolding.
    bindSubstrate(svg);

    // Inject the substrate stylesheet AFTER binding. Its [data-prim-id] and
    // [data-prim-ink] rules match the very attributes the bind walk writes, so
    // with the sheet present each setAttribute invalidated style and forced a
    // synchronous recalc on the next getBBox / getComputedStyle — ~157ms of
    // layout thrash across ~660 primitives on load. The bind decisions read the
    // vendor SVG's own fill/stroke (not our engrave overrides), so deferring the
    // sheet changes nothing about what binds; it is still injected synchronously
    // before first paint, so the rendered result is byte-identical.
    injectSubstrateStyles(svg);

    // The channel-coloured cap fill must sit BEHIND each knob's pointer
    // triangle and ring markings, not over them. Binding picks the outer circle
    // on some knobs and the inner on others, so a filled cap can occlude its own
    // pointer (the BLACK/FLARE blue dial lost its triangle this way). Sending
    // every bound encoder to the back of its group lets the later-drawn
    // markings paint on top, so every knob keeps its pointer.
    for (const enc of svg.querySelectorAll<SVGGraphicsElement>('[data-component-type="encoder"]')) {
      const parent = enc.parentNode;
      if (parent && parent.firstChild !== enc) parent.insertBefore(enc, parent.firstChild);
    }

    // Rotary encoders: a vertical drag turns the whole knob — cap, pointer and
    // rings together — about its centre, snapping through detents (Vibration API
    // where available). Each knob's parts are the cap plus the unbound marks
    // within its radius; a single rotate, expressed in the shared parent space
    // (cap transform x local centre), is prepended to each so they turn as one.
    const centreInParent = (el: SVGGraphicsElement): DOMPoint => {
      const b = el.getBBox();
      const m = el.transform.baseVal.consolidate()?.matrix;
      const p = new DOMPoint(b.x + b.width / 2, b.y + b.height / 2);
      return m ? p.matrixTransform(m) : p;
    };
    // Two rotary classes share the 'encoder' component type. ENDLESS encoders
    // (the menu SELECT and the four adjustment knobs) spin freely. Everything
    // else is a DIAL: a limited-throw control whose pointer rests at 12 o'clock
    // (neutral / zero) and travels at most to 5 o'clock clockwise and 7 o'clock
    // anticlockwise — i.e. clamped to +/-150 degrees, with a dead band across
    // the bottom. The dial set is "every encoder NOT in this endless set".
    const ENDLESS_ENCODER_IDS = new Set([
      'cmp-rotary-select',
      'cmp-rotary-adjust-1',
      'cmp-rotary-adjust-2',
      'cmp-rotary-adjust-3',
      'cmp-rotary-adjust-4',
    ]);
    const DIAL_LIMIT_DEG = 150;

    // Chassis silkscreen that falls within a knob's gather radius but is NOT part
    // of the knob — static engraving that must not rotate with it. Operator-
    // confirmed via the dev picker; add ids as they are found.
    const KNOB_PART_EXCLUDE = new Set(['p0618-path']);

    // Pass 1 — gather each knob's parts WITHOUT mutating the DOM. Grouping
    // inline corrupted later knobs: a created <g> is itself an
    // SVGGraphicsElement, so a neighbouring knob's gather would pick it up and
    // the drag wiring broke. Collect everything against the original tree first.
    const knobSpecs: {
      parent: Node;
      parts: SVGGraphicsElement[];
      centre: DOMPoint;
      isDial: boolean;
      id: string;
    }[] = [];
    for (const cap of svg.querySelectorAll<SVGGraphicsElement>('[data-component-type="encoder"]')) {
      const parent = cap.parentNode;
      if (!parent) continue;
      const isDial = !ENDLESS_ENCODER_IDS.has(cap.getAttribute('data-component-id') ?? '');
      const centre = centreInParent(cap);
      const capBox = cap.getBBox();
      const capScale = cap.transform.baseVal.consolidate()?.matrix;
      const radius =
        Math.max(capBox.width, capBox.height) *
        (capScale ? Math.hypot(capScale.a, capScale.b) : 1) *
        0.65;
      const parts: SVGGraphicsElement[] = [];
      for (const sib of Array.from(parent.childNodes)) {
        if (!(sib instanceof SVGGraphicsElement)) continue;
        if (sib !== cap && sib.hasAttribute('data-component-id')) continue;
        if (KNOB_PART_EXCLUDE.has(sib.getAttribute('data-prim-id') ?? '')) continue;
        let pc: DOMPoint;
        try {
          pc = centreInParent(sib);
        } catch {
          continue;
        }
        if (Math.hypot(pc.x - centre.x, pc.y - centre.y) <= radius) parts.push(sib);
      }
      knobSpecs.push({
        parent,
        parts,
        centre,
        isDial,
        id: cap.getAttribute('data-component-id') ?? '',
      });
    }

    // Pass 2 — wrap each knob's parts in a rotation group and turn the group as
    // one on a vertical drag, snapping through detents (Vibration where present).
    for (const { parent, parts, centre, isDial, id } of knobSpecs) {
      const first = parts[0];
      if (!first) continue;
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      parent.insertBefore(group, first);
      for (const el of parts) group.appendChild(el);
      const rot = svg.createSVGTransform();
      rot.setRotate(0, centre.x, centre.y);
      group.transform.baseVal.appendItem(rot);
      group.style.cursor = 'ns-resize';
      const DETENT_DEG = 12;
      let active = false;
      let startY = 0;
      let baseAngle = 0;
      let angle = 0;
      const onMove = (ev: PointerEvent): void => {
        if (!active) return;
        let snapped = Math.round((baseAngle + (startY - ev.clientY) * 1.6) / DETENT_DEG) * DETENT_DEG;
        // Dials are limited-throw: 12 o'clock is zero, travel stops at 5 o'clock
        // (+150) clockwise and 7 o'clock (-150) anticlockwise. Endless encoders
        // keep spinning.
        if (isDial) snapped = Math.max(-DIAL_LIMIT_DEG, Math.min(DIAL_LIMIT_DEG, snapped));
        if (snapped !== angle) {
          angle = snapped;
          rot.setRotate(angle, centre.x, centre.y);
          if ('vibrate' in navigator) navigator.vibrate(4);
          this.#applyPaintDial(id, angle);
        }
      };
      const onUp = (): void => {
        active = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      group.addEventListener('pointerdown', (ev) => {
        active = true;
        startY = ev.clientY;
        baseAngle = angle;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        ev.preventDefault();
      });
    }

    // Replace Sony's vendor-outlined silkscreen labels with live type in the
    // canonical UI face. getScreenCTM / getBBox are only valid once the browser
    // has laid the freshly-mounted SVG out, so defer one animation frame —
    // running synchronously here measured some glyphs before layout, which made
    // the result both incomplete and non-deterministic between loads.
    requestAnimationFrame(() => {
      renderDerivedLabels(svg);
      // Dev picker — OFF by default so the panel renders clean. Toggle with
      // ?debug=on (…/#/panels/sony/rcp-3500?debug=on); ?debug=off or no flag
      // hides it. enableInteractiveSelect also un-hides the IGNORED primitives
      // so even already-removed features can be re-marked.
      if (candidateOverlayRequested()) {
        drawCandidateOverlay(svg);
        enableInteractiveSelect(svg);
      }
    });

    // Hard-plastic press feedback. The panel is viewed straight down (the
    // 2D/3D top elevation), so a press must read as the cap SINKING — a
    // slight shrink about its own centre — not as the cap sliding down the
    // screen. A CSS transform on the cap would replace its placement
    // matrix and fling it to the origin, so the shrink goes on a wrapper
    // <g> (as the encoder rotation does): the wrapper carries no transform
    // of its own, leaving the cap's matrix intact. The scale pivots on the
    // cap centre measured in the wrapper's parent space. data-pressed stays
    // on the cap to drive the brightness dim in the stylesheet.
    const PRESS_SCALE = 0.965;
    for (const btn of svg.querySelectorAll<SVGGraphicsElement>(
      '[data-component-type="button"]',
    )) {
      const parent = btn.parentNode;
      if (!parent) continue;
      const wrap = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      wrap.dataset['pressWrap'] = '';
      parent.insertBefore(wrap, btn);
      wrap.appendChild(btn);
      const box = btn.getBBox();
      const placement = btn.transform.baseVal.consolidate()?.matrix;
      const c = new DOMPoint(box.x + box.width / 2, box.y + box.height / 2);
      const centre = placement ? c.matrixTransform(placement) : c;
      const sink = svg.createSVGTransform();
      sink.setMatrix(svg.createSVGMatrix());
      wrap.transform.baseVal.appendItem(sink);
      const press = () => {
        sink.setMatrix(
          svg
            .createSVGMatrix()
            .translate(centre.x, centre.y)
            .scale(PRESS_SCALE)
            .translate(-centre.x, -centre.y),
        );
        btn.setAttribute('data-pressed', '');
      };
      const release = () => {
        sink.setMatrix(svg.createSVGMatrix());
        btn.removeAttribute('data-pressed');
      };
      btn.addEventListener('pointerdown', press);
      btn.addEventListener('pointerup', release);
      btn.addEventListener('pointerleave', release);
      btn.addEventListener('pointercancel', release);
    }

    // Click-to-toggle the illumination, so every key can be previewed lit and
    // unlit. Flipping data-component-state is all that is needed — the
    // stylesheet already paints on/lit with each cap's --component-illum (and a
    // soft amber fallback for any key without a measured colour). Covers both
    // ordinary and assignable buttons; the press-sink above gives the click its
    // tactile feedback.
    //
    // The physically-coloured caps are moulded in coloured hard plastic, so
    // they read coloured even when OFF — a dead panel still has an orange PARA
    // key, it simply does not glow. Each is tagged with its colour family
    // (data-cap-family), which the stylesheet maps to the calibrated moulded
    // and emissive colours. Matched on the inventory label, normalised the way
    // svg-substrate does (lower-case, alphanumerics only).
    const capFamily: Record<string, string> = {
      para: 'amber', master: 'amber', subordinate: 'amber',
      campw: 'green', standard: 'green', irismbactive: 'green', panelactive: 'green',
      startbreak: 'red', white: 'red', black: 'red', store: 'red', call: 'red',
    };
    const normCap = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const isLit = (s: string | null): boolean => s === 'on' || s === 'lit';
    for (const key of svg.querySelectorAll<SVGGraphicsElement>(
      '[data-component-type="button"], [data-component-type="assignable-button"]',
    )) {
      key.style.cursor = 'pointer';
      // Only tag a cap that actually carries an illumination colour, so a
      // same-labelled but uncoloured key (the white-balance WHITE) stays
      // off-white rather than inheriting the red family.
      const fam = capFamily[normCap(key.getAttribute('data-component-label') ?? '')];
      if (fam && key.style.getPropertyValue('--component-illum').trim() !== '') {
        key.setAttribute('data-cap-family', fam);
      }
      key.addEventListener('click', () => {
        key.setAttribute('data-component-state', isLit(key.getAttribute('data-component-state')) ? 'off' : 'lit');
      });
    }

    // Capture every bound key/LED's resting state so returning to 'on' restores
    // the exact powered default, then apply the current power mode.
    for (const el of svg.querySelectorAll(
      '[data-component-type="button"], [data-component-type="assignable-button"], [data-component-type="led"]',
    )) {
      this.#defaultStates.set(el, el.getAttribute('data-component-state') ?? 'off');
    }
    this.#applyMode();

    // IRIS lever throw. The RCP-3500 iris control is a single-axis lever
    // (Operating Instructions p.14): vertical travel drives the iris, and the
    // lever springs back to neutral on release. Dragging the cap translates it
    // along its throw; letting go animates it home. Screen-pixel deltas are
    // converted to the element's local units through its screen CTM so the
    // travel tracks the pointer one-to-one regardless of panel scale.
    const iris = svg.querySelector<SVGGraphicsElement>('[data-component-type="iris-control"]');
    // Move the WHOLE lever assembly (the wrapper <g> built in bindSubstrate), not
    // just the cap fill — otherwise the fill slid off the rings and the scale
    // bled through. Falls back to the cap alone if the wrapper is absent.
    const lever = svg.querySelector<SVGGraphicsElement>('[data-iris-lever-group]') ?? iris;
    if (iris && lever) {
      const screenCtm = lever.getScreenCTM();
      const pxToLocal = screenCtm ? 1 / Math.hypot(screenCtm.c, screenCtm.d) : 1;
      const THROW = 16; // local units of travel each way
      iris.style.cursor = 'ns-resize';
      // The iris lever sets an ABSOLUTE iris value: it stays exactly where it is
      // left and does NOT spring back to neutral (that was wrong physics). Track
      // the held offset and add each drag's delta to it, so re-grabbing the lever
      // never jumps. Travel is clamped to +/- THROW. No transition — the lever
      // tracks the hand 1:1 while dragging and simply holds on release.
      let active = false;
      let startY = 0;
      let offset = 0;
      let startOffset = 0;
      const onMove = (ev: PointerEvent): void => {
        if (!active) return;
        offset = Math.max(-THROW, Math.min(THROW, startOffset + (ev.clientY - startY) * pxToLocal));
        lever.style.transform = `translateY(${String(offset)}px)`;
      };
      const onUp = (): void => {
        active = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      iris.addEventListener('pointerdown', (ev) => {
        active = true;
        startY = ev.clientY;
        startOffset = offset;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        ev.preventDefault();
      });
    }
  }

  override updated(changed: PropertyValues): void {
    if (changed.has('mode')) this.#applyMode();
  }

  /**
   * Drive every bound key and LED in the SVG substrate to match the power
   * mode. OFF → all states 'off' (moulded caps keep their plastic colour via
   * CSS but nothing glows). TEST → all lit ('lit' for keys, 'on' for LEDs).
   * ON → each control's captured resting default. The unbound indicator LEDs
   * are handled by CSS (.panel[data-mode]) and the displays by render(), both
   * keyed off the same mode.
   */
  #applyMode(): void {
    const svg = this.renderRoot.querySelector<SVGSVGElement>('.panel svg');
    if (!svg) return;
    for (const el of svg.querySelectorAll(
      '[data-component-type="button"], [data-component-type="assignable-button"], [data-component-type="led"]',
    )) {
      const isLed = el.getAttribute('data-component-type') === 'led';
      const next =
        this.mode === 'off'
          ? 'off'
          : this.mode === 'test'
            ? isLed
              ? 'on'
              : 'lit'
            : (this.#defaultStates.get(el) ?? 'off');
      el.setAttribute('data-component-state', next);
    }
    if (this.mode === 'test') this.#startLedBlink(svg);
    else this.#stopLedBlink(svg);
  }

  /**
   * TEST lamp-test: every half-second, step each indicator LED through its
   * available states (data-led-set) with an unlit beat between each, so the
   * operator can confirm every colour of every LED. Driven imperatively with an
   * inline !important fill — the only thing that overrides the LED stylesheet
   * rules — rather than a CSS animation. Single-colour sets (amber, green) blink
   * colour/off; the green-amber-red set cycles all three with off between.
   */
  #startLedBlink(svg: SVGSVGElement): void {
    if (this.#ledBlinkId) return;
    const OFF = 'rgba(8, 10, 12, 0.85)';
    const SEQ: Record<string, readonly (string | null)[]> = {
      amber: ['#e8a83a', null],
      green: ['#20c038', null],
      gar: ['#20c038', null, '#e8a83a', null, '#ff2a1a', null],
    };
    const leds = [...svg.querySelectorAll<SVGGraphicsElement>('[data-led-set]')];
    let tick = 0;
    const step = (): void => {
      for (const el of leds) {
        const seq = SEQ[el.getAttribute('data-led-set') ?? 'amber'] ?? SEQ['amber'];
        if (!seq) continue;
        const c = seq[tick % seq.length];
        if (c) {
          el.style.setProperty('fill', c, 'important');
          el.style.setProperty('filter', `drop-shadow(0 0 0.5cqi ${c})`, 'important');
        } else {
          el.style.setProperty('fill', OFF, 'important');
          el.style.setProperty('filter', 'none', 'important');
        }
      }
      // Caps stay STEADILY lit in TEST (set once by #applyMode); only the LEDs
      // blink. Toggling every cap on a timer forced a filter repaint across the
      // whole key matrix every 500ms, which made the panel painfully heavy on
      // lower-powered machines — so the lamp test now lights the keys and
      // animates only the indicator LEDs.
      tick += 1;
    };
    // Frame-aligned via requestAnimationFrame with a 500ms accumulator rather
    // than setInterval: rAF is paused while the tab is hidden, so the lamp test
    // stops burning paint and CPU in the background and stays in step with the
    // compositor. step() runs once up front so the LEDs light immediately.
    step();
    let last = 0;
    const loop = (now: number): void => {
      if (!last) last = now;
      if (now - last >= 500) {
        last = now;
        step();
      }
      this.#ledBlinkId = requestAnimationFrame(loop);
    };
    this.#ledBlinkId = requestAnimationFrame(loop);
  }

  #stopLedBlink(svg: SVGSVGElement): void {
    if (this.#ledBlinkId) {
      cancelAnimationFrame(this.#ledBlinkId);
      this.#ledBlinkId = 0;
    }
    for (const el of svg.querySelectorAll<SVGGraphicsElement>('[data-led-set]')) {
      el.style.removeProperty('fill');
      el.style.removeProperty('filter');
    }
  }

  /**
   * Map a paint dial's rotation to its LCD value. The dial throw is ±150°
   * with 12 o'clock as neutral, mapped onto the Sony ±99 paint range — so a
   * knob at rest reads 0 and full clockwise reads +99. Knobs with no paint
   * mapping (menu SELECT, the adjustment knobs, iris SENS/COARSE, ASSIGNABLE)
   * fall through and leave the readout untouched.
   */
  #applyPaintDial(id: string, angleDeg: number): void {
    const value = Math.round((angleDeg / 150) * 99);
    const { white, black } = this.paint;
    switch (id) {
      case 'cmp-rotary-white-r':
        this.paint = { ...this.paint, white: { ...white, r: value } };
        break;
      case 'cmp-rotary-white-g':
        this.paint = { ...this.paint, white: { ...white, g: value } };
        break;
      case 'cmp-rotary-white-b':
        this.paint = { ...this.paint, white: { ...white, b: value } };
        break;
      case 'cmp-rotary-black-r':
        this.paint = { ...this.paint, black: { ...black, r: value } };
        break;
      case 'cmp-rotary-black-g':
        this.paint = { ...this.paint, black: { ...black, g: value } };
        break;
      case 'cmp-rotary-black-b':
        this.paint = { ...this.paint, black: { ...black, b: value } };
        break;
      case 'cmp-rotary-detail':
        this.paint = { ...this.paint, detail: value };
        break;
      default:
        break;
    }
  }

  #setMode(mode: PowerMode): void {
    this.mode = mode;
  }

  #renderPowerSwitch() {
    const positions: readonly { mode: PowerMode; label: string }[] = [
      { mode: 'test', label: 'TEST' },
      { mode: 'on', label: 'ON' },
      { mode: 'off', label: 'OFF' },
    ];
    return html`
      <div class="power">
        <div class="power-title">POWER</div>
        <div
          class="power-switch"
          data-mode=${this.mode}
          role="radiogroup"
          aria-label="Panel power mode"
        >
          ${positions.map(
            (p) => html`
              <button
                type="button"
                class="power-pos"
                data-pos=${p.mode}
                role="radio"
                aria-checked=${this.mode === p.mode}
                @click=${() => {
                  this.#setMode(p.mode);
                }}
              >
                <span class="power-dot"></span>
                <span class="power-label">${p.label}</span>
              </button>
            `,
          )}
        </div>
      </div>
    `;
  }

  override render() {
    return html`
      <div class="stage">
        ${this.#renderPowerSwitch()}
        <div class="panel" data-mode=${this.mode}>
          ${DISPLAY_OVERLAYS.map((overlay) => this.#renderDisplayOverlay(overlay))}
        </div>
      </div>
    `;
  }

  #renderDisplayOverlay(overlay: DisplayOverlay) {
    const tag = unsafeStatic('panel-display');
    // z-index lifts the display content ABOVE the substrate SVG (which is the
    // last child of .panel and would otherwise paint its dark window over the
    // digits/unlit ghost). pointer-events stay off so clicks reach the controls.
    const style = `position:absolute;left:${overlay.left}%;top:${overlay.top}%;width:${overlay.width}%;height:${overlay.height}%;pointer-events:none;z-index:1;`;
    // Power mode overrides each display's own mode: OFF blanks every screen,
    // TEST fills it (colour bars / all segments), ON shows its normal content.
    const effectiveMode =
      this.mode === 'off' ? 'off' : this.mode === 'test' ? 'test' : overlay.mode;
    return staticHtml`<${tag}
      style=${style}
      kind=${overlay.kind}
      mode=${effectiveMode}
      placeholder=${overlay.placeholder ?? ''}
      text=${overlay.text ?? ''}
      max-chars=${overlay.maxChars ?? 0}
      .paint=${overlay.placeholder === 'rcp-paint-status' ? this.paint : null}
    ></${tag}>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'panel-rcp-3500': PanelRcp3500Element;
  }
}
